import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { type PointerEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { useShopContent } from '@/features/storefront/content/useContent';
import { cn } from '@/lib/cn';

/**
 * The width class every rail item wears.
 *
 * A whole fraction of the row rather than a fixed width, so the row always ends
 * on a complete card - a tile sliced off by the page edge reads as a broken
 * layout, not as an invitation to scroll. The arrows carry that invitation.
 */
export const RAIL_ITEM =
  'w-[calc((100%_-_(var(--rail-across)_-_1)_*_var(--rail-gap,1rem))_/_var(--rail-across))] shrink-0 snap-start';

/** How many items a rail shows at once, by breakpoint, unless the caller says otherwise. */
const RAIL_ACROSS = '[--rail-across:2] sm:[--rail-across:3] md:[--rail-across:4] lg:[--rail-across:5]';

/**
 * A row of things a shopper can push sideways through.
 *
 * Just the mechanics: the snapping row, the measuring, and the two arrows that
 * appear only when there is something past the edge. What goes in the row is
 * the caller's business - product tiles on the storefront, tiles with their own
 * buttons on the account page - and so is the heading above it, because a rail
 * under a product and a rail under a shopper's address are headed at different
 * volumes.
 *
 * Children must be `<li>` elements wearing {@link RAIL_ITEM}.
 */
export function Rail({
  heading,
  label,
  itemCount,
  className,
  across = RAIL_ACROSS,
  arrows = 'round',
  progress = false,
  children,
}: {
  /** Rendered to the left of the arrows. Headed by the caller, at its own weight. */
  heading: ReactNode;
  /** Names the row for the paging buttons' accessible labels. */
  label: string;
  /**
   * How many items are in the row. The row is remeasured when this changes -
   * `children` cannot be compared between renders, and re-attaching the
   * observer on every render to find out would cost more than it saves.
   */
  itemCount: number;
  className?: string;
  /**
   * Classes setting `--rail-across` (and optionally `--rail-gap`) per
   * breakpoint - how many items are in view, and how far apart.
   */
  across?: string;
  /** Round outlined chevrons, or bare arrows standing on their own. */
  arrows?: 'round' | 'bare';
  /**
   * A bar under the row in place of the arrows: how much of the row is in
   * view and where. Its thumb drags the row along; a press beside the thumb
   * jumps there.
   */
  progress?: boolean;
  children: ReactNode;
}) {
  const railRef = useRef<HTMLUListElement>(null);
  const copy = useShopContent();
  // Both false means the row fits and the arrows stay away entirely.
  const [reach, setReach] = useState({ back: false, forward: false });
  // The thumb: where the view starts and how much of the row it spans, as fractions.
  const [view, setView] = useState({ start: 0, size: 1 });

  const measure = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    const furthest = rail.scrollWidth - rail.clientWidth;
    // In a right-to-left page scrollLeft runs from 0 down to -furthest, so
    // measure distance travelled rather than the raw offset.
    const travelled = Math.abs(rail.scrollLeft);
    // A pixel of slack: sub-pixel widths leave scrollLeft a hair off the end.
    setReach({ back: travelled > 1, forward: travelled < furthest - 1 });
    if (rail.scrollWidth > 0) {
      setView({ start: travelled / rail.scrollWidth, size: rail.clientWidth / rail.scrollWidth });
    }
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    measure();
    // How many cards fit changes with the viewport, and so does what is left
    // to reach - remeasure on resize rather than only on scroll.
    const observer = new ResizeObserver(measure);
    observer.observe(rail);
    return () => observer.disconnect();
  }, [measure, itemCount]);

  /** Push the row along by one screenful, snapping to the next whole card. */
  const page = (direction: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    const sign = getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
    rail.scrollBy({ left: sign * direction * rail.clientWidth, behavior: 'smooth' });
  };

  // Dragging the progress bar: where on the thumb it was grabbed, as a
  // fraction of the bar, while a drag is under way.
  const grab = useRef<number | null>(null);
  const [dragging, setDragging] = useState(false);

  /** Where a pointer stands along the bar, 0 at its start and 1 at its end. */
  const along = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const at = box.width > 0 ? (event.clientX - box.left) / box.width : 0;
    const rail = railRef.current;
    // The bar runs the way the row does: right to left in an RTL page.
    return rail && getComputedStyle(rail).direction === 'rtl' ? 1 - at : at;
  };

  /** Scrolls the row so the thumb's start stands at `start` (a fraction). */
  const scrollTo = (start: number) => {
    const rail = railRef.current;
    if (!rail) return;
    const sign = getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
    const furthest = rail.scrollWidth - rail.clientWidth;
    rail.scrollLeft = sign * Math.max(0, Math.min(furthest, start * rail.scrollWidth));
  };

  const startDrag = (event: PointerEvent<HTMLDivElement>) => {
    const rail = railRef.current;
    if (!rail) return;
    // Optional: an older browser (or a test's DOM) without capture still drags.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const at = along(event);
    // Grabbed on the thumb, it keeps its hold; pressed beside it, the thumb
    // jumps to centre under the pointer and the drag carries on from there.
    const onThumb = at >= view.start && at <= view.start + view.size;
    grab.current = onThumb ? at - view.start : view.size / 2;
    // Snapping fights a drag - every move would be pulled back to a card.
    rail.style.scrollSnapType = 'none';
    setDragging(true);
    if (!onThumb) scrollTo(at - grab.current);
  };

  const drag = (event: PointerEvent<HTMLDivElement>) => {
    if (grab.current === null) return;
    scrollTo(along(event) - grab.current);
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (grab.current === null) return;
    grab.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const rail = railRef.current;
    // Snapping back on settles the row on the nearest whole card.
    if (rail) rail.style.scrollSnapType = '';
  };

  const overflows = reach.back || reach.forward;
  const bare = arrows === 'bare';
  const Back = bare ? ArrowLeft : ChevronLeft;
  const Forward = bare ? ArrowRight : ChevronRight;

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-4">
        {heading}
        {overflows && !progress ? (
          <div className="hidden items-center gap-2 sm:flex">
            <RailArrow
              label={copy('common.rail_previous', { label })}
              disabled={!reach.back}
              onClick={() => page(-1)}
              bare={bare}
            >
              <Back className={cn('rtl-flip', bare ? 'size-6' : 'size-4')} aria-hidden />
            </RailArrow>
            <RailArrow
              label={copy('common.rail_more', { label })}
              disabled={!reach.forward}
              onClick={() => page(1)}
              bare={bare}
            >
              <Forward className={cn('rtl-flip', bare ? 'size-6' : 'size-4')} aria-hidden />
            </RailArrow>
          </div>
        ) : null}
      </div>
      <ul
        ref={railRef}
        onScroll={measure}
        className={cn(
          'no-scrollbar mt-4 flex snap-x snap-mandatory gap-[var(--rail-gap,1rem)] overflow-x-auto pb-1',
          across,
        )}
      >
        {children}
      </ul>
      {progress && overflows ? (
        // A taller strip than the bar it holds, so it is easy to grab.
        <div
          data-rail-progress=""
          aria-hidden
          className="mx-auto mt-6 w-full max-w-xl cursor-grab touch-none select-none py-2 active:cursor-grabbing"
          onPointerDown={startDrag}
          onPointerMove={drag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div
            className="relative h-1.5 overflow-hidden rounded-full"
            style={{ backgroundColor: 'var(--storefront-surface)' }}
          >
            <span
              className={cn(
                'absolute inset-y-0 rounded-full',
                !dragging && 'transition-[left,width] duration-200',
              )}
              style={{
                left: `${view.start * 100}%`,
                width: `${view.size * 100}%`,
                backgroundColor: 'var(--storefront-primary)',
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** One of a rail's two paging controls. Decoration - the row scrolls without it. */
function RailArrow({
  label,
  disabled,
  onClick,
  bare,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  bare: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex size-8 items-center justify-center transition disabled:opacity-35',
        !bare && 'rounded-full border',
      )}
      style={bare ? undefined : { borderColor: 'var(--storefront-surface)' }}
    >
      {children}
    </button>
  );
}
